/**
 * Plan events as times, links and calendar files. Pure: no React, no
 * Firebase, so the date math is testable without a device clock or a zone.
 *
 * An event stores two instants (`startsAt`, `endsAt`) and the IANA zone the
 * organizer meant ("America/Los_Angeles"). People read and type local
 * wall-clock time, so this file converts both ways without a date library,
 * using Intl to ask what the zone's offset is at a given moment.
 */
import { DEFAULT_EVENT_TIME_ZONE } from '../schemas/firestoreSchemas';
import type { PlanEventMeetingPlace } from '../types';

interface TimestampLike {
  toMillis(): number;
}

/** The slice of a PlanEvent this file reads. A real PlanEvent satisfies it. */
export interface EventTimes {
  startsAt: TimestampLike;
  endsAt: TimestampLike;
  timeZone: string;
}

export interface CalendarEvent extends EventTimes {
  id: string;
  title: string;
  status: 'scheduled' | 'cancelled';
  meetingPlace: PlanEventMeetingPlace;
}

export interface EventForLabel extends EventTimes {
  goingCount: number;
}

export interface EventForLinks {
  links: { planId: string; bundleId: string }[];
  status: 'scheduled' | 'cancelled';
  endsAt: TimestampLike;
  startsAt: TimestampLike;
}

// ----- zones -----------------------------------------------------------------

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function wallClockFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClockIn(timeZone: string, ms: number): WallClock {
  const parts = wallClockFormatter(timeZone).formatToParts(new Date(ms));
  const read = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    return part ? Number(part.value) : 0;
  };
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  };
}

/** Milliseconds the zone is ahead of UTC at instant `ms` (negative west of Greenwich). */
function zoneOffsetMs(timeZone: string, ms: number): number {
  const w = wallClockIn(timeZone, ms);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

const DATE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_SHAPE = /^(\d{2}):(\d{2})$/;

/**
 * The UTC instant when the wall clock in `timeZone` reads `dateStr` at
 * `timeStr`. Returns null for text that is not a real date and time.
 *
 * Two passes, because the offset itself depends on the answer: guess with
 * the offset at the naive instant, then ask again at the guess. When the
 * clock skips an hour in spring (02:30 on Mar 14 2027 does not exist in Los
 * Angeles) the result lands an hour later, at 03:30, the way phones do it.
 * When the clock repeats an hour in fall, the first pass wins (the earlier,
 * daylight-time one).
 */
export function zonedLocalToUtc(
  dateStr: string,
  timeStr: string,
  timeZone: string = DEFAULT_EVENT_TIME_ZONE
): Date | null {
  const d = DATE_SHAPE.exec(dateStr);
  const t = TIME_SHAPE.exec(timeStr);
  if (!d || !t) return null;
  const [year, month, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const [hour, minute] = [Number(t[1]), Number(t[2])];
  if (hour > 23 || minute > 59) return null;
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(naive);
  // Feb 30 rolls into March; reject it rather than quietly move the event.
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1) return null;
  if (check.getUTCDate() !== day) return null;

  let guess: number;
  try {
    const first = naive - zoneOffsetMs(timeZone, naive);
    const second = naive - zoneOffsetMs(timeZone, first);
    guess = second;
    // A time inside the spring gap round-trips to a different wall clock.
    const back = wallClockIn(timeZone, second);
    if (back.hour !== hour || back.minute !== minute || back.day !== day) guess = first;
  } catch {
    return null;
  }
  return new Date(guess);
}

/** The inverse of zonedLocalToUtc: the zone's date and time for an instant, in form shape. */
export function utcToZonedLocal(
  date: Date,
  timeZone: string = DEFAULT_EVENT_TIME_ZONE
): { date: string; time: string } {
  const w = wallClockIn(timeZone, date.getTime());
  const p2 = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${String(w.year).padStart(4, '0')}-${p2(w.month)}-${p2(w.day)}`,
    time: `${p2(w.hour)}:${p2(w.minute)}`,
  };
}

// ----- wording ---------------------------------------------------------------

// Newer ICU puts a narrow no-break space before AM/PM. Plain spaces read the
// same and survive copy, search and string compare.
function plainSpaces(text: string): string {
  return text.replace(/[  ]/g, ' ');
}

function dayText(ms: number, timeZone: string, locale: string, withYear: boolean): string {
  return plainSpaces(
    new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
    }).format(new Date(ms))
  );
}

function clockText(ms: number, timeZone: string, locale: string): string {
  return plainSpaces(
    new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(ms))
  );
}

function zoneYear(ms: number, timeZone: string): number {
  return wallClockIn(timeZone, ms).year;
}

/**
 * "Sat, Nov 6 · 8:00 AM to 12:00 PM" in the event's own zone, so the page
 * reads the same for a volunteer in another time zone. An event that ends on
 * a later day names both days. The year appears only when it is not the
 * current one (pass `now` to pin that in a test).
 */
export function formatEventWhen(
  event: EventTimes,
  locale: string = 'en-US',
  now: Date = new Date()
): string {
  const start = event.startsAt.toMillis();
  const end = event.endsAt.toMillis();
  const tz = event.timeZone || DEFAULT_EVENT_TIME_ZONE;
  const withYear = zoneYear(start, tz) !== zoneYear(now.getTime(), tz);
  const startDay = dayText(start, tz, locale, withYear);
  const endDay = dayText(end, tz, locale, withYear);
  const startClock = clockText(start, tz, locale);
  const endClock = clockText(end, tz, locale);
  if (startDay === endDay) return `${startDay} · ${startClock} to ${endClock}`;
  return `${startDay} · ${startClock} to ${endDay} · ${endClock}`;
}

/** "Sat, Nov 6" in the event's zone. */
export function formatEventDay(
  event: EventTimes,
  locale: string = 'en-US',
  now: Date = new Date()
): string {
  const start = event.startsAt.toMillis();
  const tz = event.timeZone || DEFAULT_EVENT_TIME_ZONE;
  return dayText(start, tz, locale, zoneYear(start, tz) !== zoneYear(now.getTime(), tz));
}

/** The line under a bundle: "Sat, Nov 6 · 3 going". */
export function nextEventLabel(
  event: EventForLabel,
  locale: string = 'en-US',
  now: Date = new Date()
): string {
  const day = formatEventDay(event, locale, now);
  // Read on the bundle's status line, after who's assigned, so it names
  // itself. Nobody signed up yet is the normal state; it says nothing.
  return event.goingCount > 0 ? `Event ${day} · ${event.goingCount} going` : `Event ${day}`;
}

// ----- which events count ------------------------------------------------------

/** A scheduled event that has not finished yet (one under way counts). */
export function isEventUpcoming(
  event: Pick<EventForLinks, 'status' | 'endsAt'>,
  now: Date = new Date()
): boolean {
  return event.status === 'scheduled' && event.endsAt.toMillis() > now.getTime();
}

function linksTo(event: EventForLinks, planId: string, bundleId: string): boolean {
  return event.links.some((l) => l.planId === planId && l.bundleId === bundleId);
}

/** The bundle's upcoming events, soonest first. */
export function upcomingEventsForBundle<E extends EventForLinks>(
  events: E[],
  planId: string,
  bundleId: string,
  now: Date = new Date()
): E[] {
  return events
    .filter((e) => linksTo(e, planId, bundleId) && isEventUpcoming(e, now))
    .sort((a, b) => a.startsAt.toMillis() - b.startsAt.toMillis());
}

/** The soonest upcoming event for a bundle, or null. */
export function nextEventForBundle<E extends EventForLinks>(
  events: E[],
  planId: string,
  bundleId: string,
  now: Date = new Date()
): E | null {
  return upcomingEventsForBundle(events, planId, bundleId, now)[0] ?? null;
}

/** Ids of this plan's bundles that have an upcoming scheduled event. */
export function bundleIdsWithUpcomingEvents(
  events: EventForLinks[],
  planId: string,
  now: Date = new Date()
): Set<string> {
  const ids = new Set<string>();
  for (const event of events) {
    if (!isEventUpcoming(event, now)) continue;
    for (const link of event.links) {
      if (link.planId === planId) ids.add(link.bundleId);
    }
  }
  return ids;
}

/** Every event linked to the bundle: upcoming first (soonest), then the rest (newest first). */
export function sortEventsForList<E extends EventForLinks>(
  events: E[],
  now: Date = new Date()
): E[] {
  const upcoming = events
    .filter((e) => isEventUpcoming(e, now))
    .sort((a, b) => a.startsAt.toMillis() - b.startsAt.toMillis());
  const rest = events
    .filter((e) => !isEventUpcoming(e, now))
    .sort((a, b) => b.startsAt.toMillis() - a.startsAt.toMillis());
  return [...upcoming, ...rest];
}

// ----- links -------------------------------------------------------------------

/** Google Maps search by coordinates when the event has them, else by the place's name. */
export function directionsUrl(meetingPlace: PlanEventMeetingPlace): string {
  const hasPoint = typeof meetingPlace.lat === 'number' && typeof meetingPlace.lng === 'number';
  const query = hasPoint ? `${meetingPlace.lat},${meetingPlace.lng}` : meetingPlace.label;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function utcStamp(ms: number): string {
  const d = new Date(ms);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return (
    `${String(d.getUTCFullYear()).padStart(4, '0')}${p2(d.getUTCMonth() + 1)}${p2(d.getUTCDate())}` +
    `T${p2(d.getUTCHours())}${p2(d.getUTCMinutes())}${p2(d.getUTCSeconds())}Z`
  );
}

/** Opens Google Calendar's "add event" screen with the event filled in. */
export function googleCalendarUrl(
  event: Pick<CalendarEvent, 'title' | 'startsAt' | 'endsAt' | 'meetingPlace'>,
  details: string
): string {
  const params = [
    ['action', 'TEMPLATE'],
    ['text', event.title],
    ['dates', `${utcStamp(event.startsAt.toMillis())}/${utcStamp(event.endsAt.toMillis())}`],
    ['details', details],
    ['location', event.meetingPlace.label],
  ];
  // Google reads the slash in `dates` literally; encoding it as %2F also works
  // but is not how its own links look.
  const query = params
    .map(([k, v]) => `${k}=${k === 'dates' ? v : encodeURIComponent(v)}`)
    .join('&');
  return `https://calendar.google.com/calendar/render?${query}`;
}

// ----- .ics (RFC 5545) -----------------------------------------------------------

/** TEXT values escape backslash, semicolon, comma and newlines. */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\r|\n/g, '\\n');
}

function utf8Length(codePoint: number): number {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
}

/**
 * Folds one content line to at most 75 octets per physical line, as RFC 5545
 * asks: break, then CRLF and one space, with the space counting toward the
 * next line's 75. Counts UTF-8 bytes and never splits a character.
 */
export function foldIcsLine(line: string): string {
  const out: string[] = [];
  let current = '';
  let bytes = 0;
  let limit = 75;
  for (const ch of line) {
    const size = utf8Length(ch.codePointAt(0) ?? 0);
    if (bytes + size > limit) {
      out.push(current);
      current = ' ';
      bytes = 1;
      limit = 75;
    }
    current += ch;
    bytes += size;
  }
  out.push(current);
  return out.join('\r\n');
}

export function buildIcs(
  event: CalendarEvent,
  options: { url: string; description: string; now?: Date }
): string {
  const stamp = utcStamp((options.now ?? new Date()).getTime());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//REDI//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${event.id}@weareredi.net`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${utcStamp(event.startsAt.toMillis())}`,
    `DTEND:${utcStamp(event.endsAt.toMillis())}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `LOCATION:${escapeIcsText(event.meetingPlace.label)}`,
    `DESCRIPTION:${escapeIcsText(options.description)}`,
    // URL is a URI value, not TEXT: no escaping.
    `URL:${options.url}`,
    `STATUS:${event.status === 'cancelled' ? 'CANCELLED' : 'CONFIRMED'}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `${lines.map(foldIcsLine).join('\r\n')}\r\n`;
}

/** A filename for the download: REDI-event-fire-break-day-2027-11-06.ics */
export function eventIcsFilename(event: Pick<CalendarEvent, 'title' | 'startsAt' | 'timeZone'>) {
  const slug =
    event.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-+|-+$)/g, '') || 'event';
  const { date } = utcToZonedLocal(new Date(event.startsAt.toMillis()), event.timeZone);
  return `REDI-event-${slug}-${date}.ics`;
}

/**
 * The text that goes in the calendar entry's description: what to bring,
 * notes, and the link back to the page. Blank parts are left out.
 */
export function eventDescription(
  event: { whatToBring?: string; notes?: string },
  url: string
): string {
  const parts: string[] = [];
  if (event.whatToBring?.trim()) parts.push(`Bring: ${event.whatToBring.trim()}`);
  if (event.notes?.trim()) parts.push(event.notes.trim());
  parts.push(url);
  return parts.join('\n\n');
}
